import {FC, useCallback, useState } from "react";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { ParkService } from "../../../services/park/parkService";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import Page from "../../../layout/Page/Page";
import CustomTable from "../../../components/table/CustomTable";
import { ActionIcon, Switch, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { menuRoutes } from "../../../router/menu";
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../components/buttons/DeletePopover";
import {ParksApiResponse } from "../../../type/entities/park-type";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import ParksFilters from "./ParksFilters";
import ParkFormModal from "../components/ParkFormModal";
import AddUsersToParkModal from "../components/AddUsersToParkModal";
import { RiUserAddLine } from "react-icons/ri";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";


interface ParksListProps { 
    setIsOpenModalForm: (value: boolean) => void;
    openModalForm: boolean;
}

const columnOrder = [
    "id",
    "company",
    "name",
    "alias",
    'capacity',
    "municipal",
    "location",
    "client",
    'parkHasUsers',
    "status",
];

const ParksList : FC<ParksListProps> = ({ setIsOpenModalForm, openModalForm}) => {
    
    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [park, setPark] = useState<any>(null);
    const [parkHasUsers, setParkHasUsers] = useState<any>(null);
    const [isOpenAddModal, setIsOpenAddModal] = useState<boolean>(false);


    // HOOKS
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new ParkService();

    // FETCHING DATA

    const [data, loading, error, refetch] = useFetch(
        useCallback(async () => {
            const response = await service.listParks(filters);
            return response.getResponseData();
        },[filters])
    );

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIA DE ESTADO UN PARQUE EÓLICO
     * @EN CHANGES THE STATUS OF A WIND FARM
     * 
     * @param id
     * @param status
     * @returns
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const toggleState = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id])
            const response = (await service.toggleStatus(id)).getResponseData();
            if(response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el parque" : "Se ha activado el parque");
            }else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        }catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UN PARQUE EÓLICO
     * @EN DELETES A WIND FARM
     * 
     * @param id
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deletePark(id)).getResponseData();
        if(response.success) {
            toast.success("Parque eliminado correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA TODOS LOS PARQUES EÓLICOS SELECCIONADOS
     * @EN DELETES ALL SELECTED WIND FARMS
     * 
     * @param ids
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiPark(ids)).getResponseData();
        if(response.success) {
            toast.success(response.message);
            refetch();
        }else {
            handleErrors(response);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------
    

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENA LOS DATOS PARA QUE COINCIDAN CON LA COLUMNA
     * @EN SORTS THE DATA TO MATCH THE COLUMN
     *
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpenModalForm(false);
        setIsOpenAddModal(false);
        setTimeout(() => {  
            setPark(null);
            setParkHasUsers(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'parks-table'}
                    divClassName='max-h-table'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['id', 'status']}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                const parkName = row.row.original.name;
                                
                                if (userCan('get', 'park')) {
                                    return (
                                        <a href={`${menuRoutes.parks.path}/${row.row.original.id}/profile/${menuRoutes.parks.profile.windTurbines}`} className='text-primary hover:text-primary hover:underline'>
                                            {parkName}
                                        </a>
                                    );
                                }
                                
                                return <span>{parkName}</span>;
                            }
                        },
                        {
                            key: 'client',
                            render: (row: any) => {
                                return row.row.original.client ? row.row.original.client.name : 'Sin cliente'
                            }
                        },
                        {
                            key: 'company',
                            permissions: [{ group: 'companies', permission: 'list' }],
                            render: (row: any) => {
                                return row.row.original.company.name
                            },
                        },
                        {
                            key: 'parkHasUsers',
                            render: (row: any) => {
                                return row.row.original.parkHasUsers.length > 0 ? (
                                        <span>{row.row.original.parkHasUsers.map((user: any) => user.user.name + ' ' + user.user.lastName).join(', ')}</span>
                                ) : (
                                       <span>Sin Coordinadores</span>
                                );
                            },
                        },
                        {
                            key: 'status',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.status ? 'Desactivar Parque' : 'Activar Parque'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.status}
                                            disabled={!userCan('edit', 'park')}
                                            onChange={() => toggleState(row.row.original.id, row.row.original.status)}
                                            switchKnobClassName='bg-red-300'
                                        />
                                    </Tooltip>
                                );
                            },
                        }
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'park', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver parque'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.parks.path}/${row.id}/profile/${menuRoutes.parks.profile.windTurbines}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-5 h-5"
                                            >
                                                <HiOutlineEye className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'park', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar parque'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setIsOpenModalForm(true);
                                                setPark(row);
                                            }}
                                        >
                                            <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Add',
                            permissions: { group: 'park', permission: 'assign_user_to_park' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Administrar Coordinadores'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setPark(row);
                                                setParkHasUsers(row.parkHasUsers);
                                                setIsOpenAddModal(true);
                                            }
                                        }
                                        >
                                            <RiUserAddLine className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'park', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar parque'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                actionIconClassName="w-5 h-5"
                                                title={`Eliminar parque`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ParksApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Parques Eólicos'}>
                <ParksFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <ParkFormModal isOpen={openModalForm} refetch={refetch} onClose={onClose} park={park} />
            <AddUsersToParkModal isOpen={isOpenAddModal} refetch={refetch} onClose={onClose} park={park} parkHasUsers={parkHasUsers} />
            
        </Page>
            
        


    )
}

export default ParksList;

